import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPO_COLORS = {
  pesaje: { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-700', label: 'Pesaje', dot: 'bg-blue-500' },
  servicio: { bg: 'bg-purple-50', border: 'border-purple-400', text: 'text-purple-700', label: 'Servicio', dot: 'bg-purple-500' },
  paricion: { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-700', label: 'Paricion', dot: 'bg-green-500' },
  control_macho: { bg: 'bg-amber-50', border: 'border-amber-400', text: 'text-amber-700', label: 'Control Macho', dot: 'bg-amber-500' },
  alerta: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-700', label: 'Alerta', dot: 'bg-red-500' },
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('es-AR'); } catch { return d; }
};

export default function HistorialPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');

  const buscar = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(
        `${API_URL}/api/animales/historial/buscar?establecimiento_id=${user.establecimiento_id}&q=${encodeURIComponent(query.trim())}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Animal no encontrado');
      }
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const animal = result?.animal;
  const resumen = result?.resumen;
  const crias = result?.crias || [];
  const timeline = (result?.timeline || []).filter(
    (ev) => filtroTipo === 'todos' || ev.tipo === filtroTipo
  );

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto" data-testid="historial-page">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Historial de Animal</h1>
      <p className="text-sm text-gray-500 mb-6">Busca por numero de caravana (VID) o identificacion electronica (IDE)</p>

      {/* Search */}
      <form onSubmit={buscar} className="flex gap-3 mb-6" data-testid="historial-search-form">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Ingresar VID o IDE..."
          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-lg"
          data-testid="historial-search-input"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading || !query.trim()}
          className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
          data-testid="historial-search-btn"
        >
          {loading ? 'Buscando...' : 'Buscar'}
        </button>
      </form>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-6" data-testid="historial-error">
          {error}
        </div>
      )}

      {animal && (
        <>
          {/* Animal card */}
          <div className="bg-white rounded-lg shadow p-5 mb-6 border-l-4 border-green-500" data-testid="historial-animal-card">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-2xl font-bold text-gray-900">VID: {animal.VID}</span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${animal.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {animal.estado}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {animal.IDE && <span>IDE: {animal.IDE}</span>}
                  <span className="capitalize">{animal.sexo}</span>
                  <span>{animal.raza}</span>
                  <span className="capitalize">{animal.categoria?.replace('_', ' ')}</span>
                  {animal.propietario && <span>Prop: {animal.propietario}</span>}
                  <span>Nac: {formatDate(animal.fecha_nacimiento)}</span>
                </div>
              </div>
              <button
                onClick={() => navigate(`/animales/${animal.id}`)}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition"
                data-testid="historial-ver-ficha-btn"
              >
                Ver Ficha Completa
              </button>
            </div>
          </div>

          {/* Summary counters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="historial-resumen">
            <div className="bg-white rounded-lg p-3 text-center shadow-sm border-t-2 border-gray-300">
              <p className="text-2xl font-bold text-gray-900">{resumen.total_eventos}</p>
              <p className="text-xs text-gray-500">Total Eventos</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center shadow-sm border-t-2 border-blue-400">
              <p className="text-2xl font-bold text-blue-600">{resumen.pesajes}</p>
              <p className="text-xs text-gray-500">Pesajes</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center shadow-sm border-t-2 border-purple-400">
              <p className="text-2xl font-bold text-purple-600">{resumen.servicios}</p>
              <p className="text-xs text-gray-500">Servicios</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center shadow-sm border-t-2 border-green-400">
              <p className="text-2xl font-bold text-green-600">{resumen.pariciones}</p>
              <p className="text-xs text-gray-500">Pariciones</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center shadow-sm border-t-2 border-emerald-400">
              <p className="text-2xl font-bold text-emerald-600">{resumen.total_crias || 0}</p>
              <p className="text-xs text-gray-500">Crias</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center shadow-sm border-t-2 border-amber-400">
              <p className="text-2xl font-bold text-amber-600">{resumen.controles_machos}</p>
              <p className="text-xs text-gray-500">Controles</p>
            </div>
            <div className="bg-white rounded-lg p-3 text-center shadow-sm border-t-2 border-red-400">
              <p className="text-2xl font-bold text-red-600">{resumen.alertas}</p>
              <p className="text-xs text-gray-500">Alertas</p>
            </div>
          </div>

          {/* Crias / Genealogy Section */}
          {crias.length > 0 && (
            <div className="bg-white rounded-lg shadow p-5 mb-6" data-testid="historial-crias">
              <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Crias ({crias.length}) - Genealogia
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-500">
                      <th className="pb-2 pr-3">#</th>
                      <th className="pb-2 pr-3">VID</th>
                      <th className="pb-2 pr-3">Sexo</th>
                      <th className="pb-2 pr-3">Raza</th>
                      <th className="pb-2 pr-3">Categoria</th>
                      <th className="pb-2 pr-3">Fecha Paricion</th>
                      <th className="pb-2 pr-3">Peso Nac.</th>
                      <th className="pb-2 pr-3">Tipo Parto</th>
                      <th className="pb-2 pr-3">Estado</th>
                      <th className="pb-2">Accion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crias.map((cria, i) => (
                      <tr key={cria.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`cria-${i}`}>
                        <td className="py-2 pr-3 text-gray-400">{i + 1}</td>
                        <td className="py-2 pr-3 font-semibold text-gray-900">{cria.VID}</td>
                        <td className="py-2 pr-3 capitalize">{cria.sexo}</td>
                        <td className="py-2 pr-3">{cria.raza}</td>
                        <td className="py-2 pr-3 capitalize">{cria.categoria?.replace('_', ' ') || '-'}</td>
                        <td className="py-2 pr-3">{formatDate(cria.fecha_paricion || cria.fecha_nacimiento)}</td>
                        <td className="py-2 pr-3">{cria.peso_nacimiento ? `${cria.peso_nacimiento} kg` : '-'}</td>
                        <td className="py-2 pr-3 capitalize">{cria.tipo_parto || '-'}</td>
                        <td className="py-2 pr-3">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${cria.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {cria.estado}
                          </span>
                        </td>
                        <td className="py-2">
                          <button onClick={() => { setQuery(cria.VID); setResult(null); }}
                            className="text-xs text-green-600 hover:underline" data-testid={`buscar-cria-${i}`}>
                            Ver historial
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="historial-filtros">
            {[
              { key: 'todos', label: 'Todos' },
              { key: 'pesaje', label: 'Pesajes' },
              { key: 'servicio', label: 'Servicios' },
              { key: 'paricion', label: 'Pariciones' },
              { key: 'control_macho', label: 'Controles' },
              { key: 'alerta', label: 'Alertas' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFiltroTipo(f.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                  filtroTipo === f.key
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`filtro-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Timeline */}
          {timeline.length === 0 ? (
            <div className="text-center py-12 text-gray-400" data-testid="historial-empty">
              <p className="text-lg">Sin eventos registrados</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="historial-timeline">
              {timeline.map((ev, i) => {
                const c = TIPO_COLORS[ev.tipo] || TIPO_COLORS.alerta;
                return (
                  <div key={i} className={`${c.bg} border-l-4 ${c.border} rounded-lg p-4 flex items-start gap-4`} data-testid={`evento-${i}`}>
                    <div className={`w-3 h-3 rounded-full ${c.dot} mt-1.5 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold uppercase ${c.text}`}>{c.label}</span>
                        <span className="text-xs text-gray-400">{formatDate(ev.fecha)}</span>
                      </div>
                      <p className="text-sm text-gray-800">{ev.detalle}</p>
                      {ev.tipo === 'pesaje' && ev.data?.gdp != null && (
                        <p className="text-xs text-gray-500 mt-1">GDP: {ev.data.gdp} kg/dia</p>
                      )}
                      {ev.tipo === 'servicio' && ev.data?.toro_vid && (
                        <p className="text-xs text-gray-500 mt-1">Toro: {ev.data.toro_vid}</p>
                      )}
                      {ev.tipo === 'paricion' && ev.data?.cria_vid && (
                        <p className="text-xs text-gray-500 mt-1">Cria VID: {ev.data.cria_vid}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {!result && !loading && !error && (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-lg">Ingresa el numero de un animal para ver su historial</p>
        </div>
      )}
    </div>
  );
}
